import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { Actions, AuthContextConsumer, RouteKey, State, Callback, ExpiredLink, Selectors } from '@grandvisionhq/auth';
import { Loader, Delayed, Button } from '@grandvisionhq/common';
import { connect, withIntl, useIntl } from '@grandvisionhq/state';
import { QueryString, StringGuard } from '@grandvisionhq/utils';
import { pageView } from '../data-layer/actions.js';
import PageWithMenu from '../PageWithMenu/PageWithMenu.js';
import Protected from '../Protected/Protected.js';
import { OnlyOnClient } from './OnlyOnClient.js';
import { LoadingData } from '../Profile/components/LoadingData.js';
import { useLink } from '@grandvisionhq/frk-routing';

/* Protected routes */ const AppointmentDetailContainer = /*#__PURE__*/ React.lazy(()=>import('../Appointments/components/AppointmentDetail/AppointmentDetailContainer.js')
);
const AddressBookContainer = /*#__PURE__*/ React.lazy(()=>import('../AddressBook/AddressBookContainer.js')
);
const Appointments = /*#__PURE__*/ React.lazy(()=>import('../Appointments/List/Appointments.js')
);
const ProfileContainer = /*#__PURE__*/ React.lazy(()=>import('../Profile/ProfileContainer.js')
);
const Orders = /*#__PURE__*/ React.lazy(()=>import('../Orders/List/OrdersContainer.js')
);
const OrderDetail = /*#__PURE__*/ React.lazy(()=>import('../Orders/Detail/OrderDetailContainer.js')
);
const Prescriptions = /*#__PURE__*/ React.lazy(()=>import('../Prescriptions/PrescriptionsPage/PrescriptionPage.js')
);
const Settings = /*#__PURE__*/ React.lazy(()=>import('../Settings/SettingsContainer.js')
);
const SubscriptionList = /*#__PURE__*/ React.lazy(()=>import('../Subscriptions/List/SubscriptionList.js')
);
const UnlinkOrderContainer = /*#__PURE__*/ React.lazy(()=>import('../Unlink/UnlinkOrderContainer.js')
);
const SubscriptionDetail = /*#__PURE__*/ React.lazy(()=>import('../Subscriptions/Detail/SubscriptionDetail.js')
);
const ChangePassword = /*#__PURE__*/ React.lazy(()=>import('../ChangePassword/ChangePassword.js')
);
const OptIns = /*#__PURE__*/ React.lazy(()=>import('../OptIns/index.js')
);
const SignUp = /*#__PURE__*/ React.lazy(()=>import('@grandvisionhq/auth').then((module)=>{
        return {
            default: module.CreateAccount.SignUp
        };
    })
);
const CreateAccountSetPassword = /*#__PURE__*/ React.lazy(()=>import('@grandvisionhq/auth').then((module)=>{
        return {
            default: module.CreateAccount.SetPassword
        };
    })
);
const SubmitEmailAddress = /*#__PURE__*/ React.lazy(()=>import('@grandvisionhq/auth').then((module)=>{
        return {
            default: module.ResetPassword.SubmitEmailAddress
        };
    })
);
const SetPassword = /*#__PURE__*/ React.lazy(()=>import('@grandvisionhq/auth').then((module)=>{
        return {
            default: module.ResetPassword.SetPassword
        };
    })
);
const Verify = /*#__PURE__*/ React.lazy(()=>import('@grandvisionhq/auth').then((module)=>{
        return {
            default: module.ResetPassword.Verify
        };
    })
);
const Login = /*#__PURE__*/ React.lazy(()=>import('@grandvisionhq/auth').then((module)=>{
        return {
            default: module.Login
        };
    })
);
class MyAccountRoot extends (React.Component) {
    static getDerivedStateFromProps(props, state) {
        const reverseRouteLookup = props.routes ? Object.entries(props.routes).reduce((reverse, [name, route])=>swcHelpers.objectSpread({}, reverse, {
                [route.toLowerCase()]: name
            })
        , {}) : {};
        const stripTrailingSlash = window.location.pathname.replace(/\/?$/, '');
        const urlParts = stripTrailingSlash.split('/');
        const newRoute = urlParts === null || urlParts === void 0 ? void 0 : urlParts[2];
        if (!newRoute || !state.reverseRouteLookup) {
            return swcHelpers.objectSpread({}, state, {
                reverseRouteLookup
            });
        }
        if (newRoute in reverseRouteLookup) {
            const path = urlParts[3] ? `${newRoute}/${urlParts[3]}` : `${newRoute}`;
            /**
       * The hash is not passed into currentRoute via setState because it is used as
       * the key for renderSubroute and adding a hash breaks internal router mapping
       */ return swcHelpers.objectSpread({}, state, {
                reverseRouteLookup,
                currentRoute: path
            });
        }
        return swcHelpers.objectSpread({}, state, {
            reverseRouteLookup
        });
    }
    componentDidMount() {
        const { isHideUserDataFromAnalyticsEnabled  } = this.props;
        this.props.pageView(this.state.currentRoute, Boolean(isHideUserDataFromAnalyticsEnabled));
    }
    componentDidUpdate(prevProps, prevState) {
        var _routes;
        const topLevelRoutes = Object.values((_routes = this.props.routes) !== null && _routes !== void 0 ? _routes : {});
        const isRouteEqual = prevState.currentRoute === this.state.currentRoute;
        const { isHideUserDataFromAnalyticsEnabled  } = this.props;
        const hasValidAuthState = State.isLoggedIn(this.props.authState) || State.isLoggedOut(this.props.authState) || State.isLoggedInPasswordless(this.props.authState);
        var ref;
        const routeExists = topLevelRoutes.some((route)=>{
            var ref1;
            return (ref = (ref1 = this.state.currentRoute) === null || ref1 === void 0 ? void 0 : ref1.includes(route)) !== null && ref !== void 0 ? ref : false;
        });
        const shouldLogPageView = !isRouteEqual && hasValidAuthState && routeExists;
        if (shouldLogPageView) {
            this.props.pageView(this.state.currentRoute, Boolean(isHideUserDataFromAnalyticsEnabled));
        }
    }
    render() {
        const { authState , staticsUrl , isAddressBookEnabled =false  } = this.props;
        if (State.isUninitialized(authState)) {
            return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(Loader, null), this.state.currentRoute, {
                isProtected: false,
                center: true
            });
        }
        // We do not want to lowercase the appointmentId, in es-masvision + es-optica2000 we still use TimeGrip which might return appointmentIds with upper- and lowercase, which we need to keep to later fetch appointments
        const currentRoute = this.state.currentRoute.startsWith('appointments') ? this.state.currentRoute : this.state.currentRoute.toLowerCase();
        let query;
        if (currentRoute === 'callback' || currentRoute === 'logged-out') {
            return /*#__PURE__*/ React.createElement(Callback, {
                type: currentRoute,
                parseHash: this.props.parseHash,
                loggedOut: this.props.loggedOut
            });
        }
        // These are the standard routes
        switch(this.state.reverseRouteLookup[currentRoute]){
            case RouteKey.CreateAccount:
                return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(React.Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                }, /*#__PURE__*/ React.createElement("div", {
                    className: "myaccount__form-wrapper"
                }, /*#__PURE__*/ React.createElement(CreateAccountSetPassword, {
                    consentItems: this.props.consentItems
                }))), this.state.currentRoute, {
                    isProtected: false,
                    center: true
                });
            case 'linkExpired':
                query = window ? QueryString.parse(window.location.search, true) : undefined;
                return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(ExpiredLink, {
                    targetAction: query === null || query === void 0 ? void 0 : query.targetAction
                }), this.state.currentRoute, {
                    isProtected: false,
                    center: true
                });
            case RouteKey.ResetPassword:
                return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(React.Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                }, /*#__PURE__*/ React.createElement("div", {
                    className: "myaccount__form-wrapper"
                }, /*#__PURE__*/ React.createElement(SubmitEmailAddress, null))), this.state.currentRoute, {
                    isProtected: false,
                    center: true
                });
            case RouteKey.Verify:
                return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(React.Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                }, /*#__PURE__*/ React.createElement("div", {
                    className: "myaccount__form-wrapper"
                }, /*#__PURE__*/ React.createElement(Verify, null))), this.state.currentRoute, {
                    isProtected: false,
                    center: true
                });
            case RouteKey.SetPassword:
                return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(React.Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                }, /*#__PURE__*/ React.createElement("div", {
                    className: "myaccount__form-wrapper"
                }, /*#__PURE__*/ React.createElement(SetPassword, null))), this.state.currentRoute, {
                    isProtected: false,
                    center: true
                });
            case RouteKey.Signup:
                return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(React.Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                }, /*#__PURE__*/ React.createElement("div", {
                    className: "myaccount__form-wrapper--double"
                }, /*#__PURE__*/ React.createElement(SignUp, null))), this.state.currentRoute, {
                    isProtected: false,
                    center: true
                });
            case RouteKey.Login:
                const { returnPath , error , email  } = window && QueryString.parse(window.location.search);
                return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(React.Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                }, /*#__PURE__*/ React.createElement("div", {
                    className: "myaccount__form-wrapper--double"
                }, /*#__PURE__*/ React.createElement(Login, {
                    returnPath: returnPath ? encodeURIComponent(StringGuard.guard('returnPath', returnPath)) : undefined,
                    createAccountRichText: this.props.createAccountRichText,
                    error: error ? StringGuard.guard('error', error) : undefined,
                    email: email ? StringGuard.guard('email', email) : undefined
                }))), this.state.currentRoute, {
                    isProtected: false,
                    center: true
                });
            case RouteKey.Prescriptions:
                {
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                        backRouteLinkAttributes: this.props.frameworkRoutingLinks.profile
                    }, /*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement(Prescriptions, null))), this.state.currentRoute);
                }
            case RouteKey.Settings:
                {
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                        backRouteLinkAttributes: this.props.frameworkRoutingLinks.profile
                    }, /*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement(Settings, {
                        showOptInsLink: authState.optIns.length > 0
                    }))), this.state.currentRoute, {
                        isProtected: true
                    });
                }
            case RouteKey.Profile:
                return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, null, /*#__PURE__*/ React.createElement(React.Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                }, /*#__PURE__*/ React.createElement(ProfileContainer, null))), this.state.currentRoute, {
                    isProtected: true
                });
            case RouteKey.AddressBook:
                if (isAddressBookEnabled) {
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                        backRouteLinkAttributes: this.props.frameworkRoutingLinks.profile
                    }, /*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement(AddressBookContainer, null))), this.state.currentRoute, {
                        isProtected: true
                    });
                } else {
                    const { formattedRoutes  } = this.props;
                    formattedRoutes && (window === null || window === void 0 ? void 0 : window.location.assign(formattedRoutes[RouteKey.Profile]));
                }
            case RouteKey.Appointments:
                {
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                        backRouteLinkAttributes: this.props.frameworkRoutingLinks.profile
                    }, /*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement(Appointments, null))), this.state.currentRoute, {
                        isProtected: true
                    });
                }
            //Todo - create <Orders /> page in nextJs-pages when moving 100% towards FE2.0 routing
            case RouteKey.Orders:
                {
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                        backRouteLinkAttributes: this.props.frameworkRoutingLinks.profile
                    }, /*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement(Orders, null))), this.state.currentRoute, {
                        isProtected: true
                    });
                }
            case RouteKey.Subscriptions:
                {
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                        backRouteLinkAttributes: this.props.frameworkRoutingLinks.profile
                    }, /*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement(SubscriptionList, null))), this.state.currentRoute, {
                        isProtected: true
                    });
                }
            default:
                var ref, ref2;
                if (currentRoute && ((ref = this.props.routes) === null || ref === void 0 ? void 0 : ref.appointments) && currentRoute.startsWith(this.props.routes.appointments)) {
                    const [_rest, appointmentId] = currentRoute.split('/');
                    if (appointmentId) {
                        return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                            backRouteLinkAttributes: this.props.frameworkRoutingLinks.appointments
                        }, /*#__PURE__*/ React.createElement(React.Suspense, {
                            fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                        }, /*#__PURE__*/ React.createElement(AppointmentDetailContainer, {
                            appointmentId: appointmentId
                        }))), this.state.currentRoute, {
                            isProtected: true
                        });
                    }
                } else if (currentRoute && this.props.routes && this.props.routes.orders && currentRoute.startsWith(this.props.routes.orders)) {
                    // orderNumber is number but we don't parse it because it has leading 0's
                    const [, orderNumber] = /\/([0-9]+)\/?$/.exec(currentRoute) || [];
                    //Todo - create <OrderDetail /> page in nextJs-pages when moving 100% towards FE2.0 routing
                    if (orderNumber) {
                        return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                            backRouteLinkAttributes: this.props.frameworkRoutingLinks.orders
                        }, /*#__PURE__*/ React.createElement(React.Suspense, {
                            fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                        }, /*#__PURE__*/ React.createElement(OrderDetail, {
                            orderNumber: orderNumber
                        }))), this.state.currentRoute, {
                            isProtected: true
                        });
                    }
                } else if (currentRoute && this.props.routes && this.props.routes.subscriptions && currentRoute.startsWith(this.props.routes.subscriptions)) {
                    /**
           * subscriptionId is a string that can have an uppercase ID, so need the
           * non-lowercase version of currentRoute here
           */ const [, subscriptionId] = /\/(\w+)\/?$/.exec(this.state.currentRoute) || [];
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                        backRouteLinkAttributes: this.props.frameworkRoutingLinks.profile
                    }, /*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement(SubscriptionDetail, {
                        staticsUrl: staticsUrl,
                        subscriptionId: subscriptionId
                    }))), this.state.currentRoute, {
                        isProtected: true
                    });
                } else if (currentRoute && ((ref2 = this.props.routes) === null || ref2 === void 0 ? void 0 : ref2.unlinkOrder) && currentRoute.startsWith(this.props.routes.unlinkOrder)) {
                    const isSuccessPage = currentRoute.includes('/success');
                    const query = window ? QueryString.parse(window.location.search) : undefined;
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement("div", {
                        className: "myaccount__form-wrapper"
                    }, /*#__PURE__*/ React.createElement(UnlinkOrderContainer, {
                        isSuccessPage: isSuccessPage,
                        orderToken: query && query.orderToken ? StringGuard.guard('query.orderToken', query.orderToken) : undefined,
                        orderNumber: query && query.orderNumber ? StringGuard.guard('query.orderNumber', query.orderNumber) : undefined
                    }))), this.state.currentRoute, {
                        isProtected: false,
                        center: true
                    });
                } else if (currentRoute && this.props.routes && currentRoute.startsWith(this.props.routes.settings) && currentRoute.includes('/communication-preferences')) {
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                        backRouteLinkAttributes: this.props.frameworkRoutingLinks.settings
                    }, /*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement(OptIns, null))), this.state.currentRoute, {
                        isProtected: true
                    });
                } else if (currentRoute && this.props.routes && currentRoute.startsWith(this.props.routes.settings) && currentRoute.includes('/change-password')) {
                    const { changePasswordResult  } = QueryString.parse(window.location.search);
                    return MyAccountRoot.renderSubroute(/*#__PURE__*/ React.createElement(PageWithMenu, {
                        backRouteLinkAttributes: this.props.frameworkRoutingLinks.settings
                    }, /*#__PURE__*/ React.createElement(React.Suspense, {
                        fallback: /*#__PURE__*/ React.createElement(LoadingData, null)
                    }, /*#__PURE__*/ React.createElement(ChangePassword, {
                        isSuccess: changePasswordResult === 'success'
                    }))), this.state.currentRoute, {
                        isProtected: true
                    });
                }
                break;
        }
        /**
     * Next.js seems to render a higher level route before it renders a lowe level route. E.g. when
     * rendering /pages/my-account/prescriptions.tsx, it renders pages/my-account/[...rest].tsx first.
     * Issue: https://github.com/vercel/next.js/issues/43209
     *
     * To minimize layout shift, we render a loading screen.
     */ // TODO: this is really a 404 but we can't return that here anymore
        return /*#__PURE__*/ React.createElement(PageLoader, null);
    }
    constructor(...args){
        super(...args);
        this.state = {
            reverseRouteLookup: {},
            currentRoute: this.props.initialRoute.replace(/\/$/, ''),
            pageViewDone: false
        };
    }
}
MyAccountRoot.renderSubroute = (component, currentRoute, options = {
    isProtected: true
})=>{
    const wrappedComponent = /*#__PURE__*/ React.createElement("div", {
        className: classnames('myaccount-root', {
            'myaccount-root--center': options.center
        })
    }, /*#__PURE__*/ React.createElement("div", {
        className: "site-container"
    }, component));
    return options.isProtected ? /**
       * Changing the key on every route change will re-mount the Protected component, triggering
       * an assertion of the user's Auth0 session.
       */ /*#__PURE__*/ React.createElement(Protected, {
        key: currentRoute
    }, wrappedComponent) : wrappedComponent;
};
const mapStateToProps = (state)=>{
    return {
        authState: Selectors.getAuth(state),
        formattedRoutes: Selectors.getFormattedRoutes(state),
        routes: Selectors.getRoutes(state)
    };
};
const mapDispatchToProps = {
    signup: Actions.CreateAccountActions.signup,
    onResend: Actions.CreateAccountActions.signup,
    loginWithPassword: Actions.LoginActions.loginWithPassword,
    parseHash: Actions.LoginActions.parseHash,
    loggedOut: Actions.LogoutActions.loggedOut,
    pageView: pageView
};
const MyAccountRootConnected = connect(mapStateToProps, mapDispatchToProps)(withIntl(withFrameworkRouting(MyAccountRoot)));
const MyAccountRootConnectedClientOnly = (props)=>{
    return /*#__PURE__*/ React.createElement(OnlyOnClient, null, /*#__PURE__*/ React.createElement(MyAccountRootConnected, swcHelpers.extends({}, props)));
};
class MyAccountRootConnectedClientOnlyWithContext extends React.PureComponent {
    render() {
        return /*#__PURE__*/ React.createElement(AuthContextConsumer, null, /*#__PURE__*/ React.createElement(MyAccountRootConnectedClientOnly, swcHelpers.extends({}, this.props)));
    }
}
function withFrameworkRouting(Component) {
    const wrappedName = Component.displayName || Component.name || 'Component';
    const WithFrameworkRouting = (props)=>{
        const { getLabel  } = useIntl();
        const profile = useLink({
            route: 'me.profile'
        });
        const appointments = useLink({
            route: 'me.appointments'
        });
        const orders = useLink({
            route: 'me.orders'
        });
        const settings = useLink({
            route: 'me.settings'
        });
        return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props, {
            frameworkRoutingLinks: {
                profile: swcHelpers.objectSpread({}, profile, {
                    label: getLabel(`account.menu.${RouteKey.Profile}`)
                }),
                appointments: swcHelpers.objectSpread({}, appointments, {
                    label: getLabel(`account.menu.${RouteKey.Appointments}`)
                }),
                orders: swcHelpers.objectSpread({}, orders, {
                    label: getLabel(`account.menu.${RouteKey.Orders}`)
                }),
                settings: swcHelpers.objectSpread({}, settings, {
                    label: getLabel(`account.menu.${RouteKey.Settings}`)
                })
            }
        }));
    };
    WithFrameworkRouting.displayName = `WithFrameworkRouting(${wrappedName})`;
    return WithFrameworkRouting;
}
function PageLoader() {
    const { getLabel  } = useIntl();
    const profileLinkProps = useLink({
        route: 'me.profile'
    });
    return /*#__PURE__*/ React.createElement("div", {
        className: "myaccount-root myaccount-root--center"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "site-container"
    }, /*#__PURE__*/ React.createElement(PageWithMenu, null, /*#__PURE__*/ React.createElement("div", {
        className: "myaccount__subpage-loader"
    }, /*#__PURE__*/ React.createElement(Loader, null), profileLinkProps && /*
               * In case the user somehow ends up on this loading page without
               * being redirected, we still offer them an option to recover.
               */ /*#__PURE__*/ React.createElement(Delayed, {
        delay: 5000
    }, /*#__PURE__*/ React.createElement(Button, swcHelpers.extends({}, profileLinkProps, {
        caption: getLabel('account.menu.accountDetails')
    })))))));
}

export { MyAccountRoot, MyAccountRootConnected, MyAccountRootConnectedClientOnly, MyAccountRootConnectedClientOnlyWithContext as default, mapDispatchToProps, mapStateToProps };
//# sourceMappingURL=MyAccountRoot.js.map
